import { useEffect, useState } from "react";
import axios from "axios";
import ExcelExportButton from "../components/ExcelExportButton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Wrench, Plus, Loader2, Play, CheckCircle2, ShieldCheck, XCircle, Clock, AlertTriangle, DollarSign, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  preventive: { label: "Preventiva", color: "bg-emerald-100 text-emerald-800" },
  corrective: { label: "Corretiva", color: "bg-amber-100 text-amber-800" },
  predictive: { label: "Preditiva", color: "bg-blue-100 text-blue-800" },
  inspection: { label: "Inspeção", color: "bg-purple-100 text-purple-800" },
};
const PRIORITY_COLORS = { low: "#94a3b8", medium: "#3b82f6", high: "#f59e0b", critical: "#dc2626" };
const STATUS_COLS = [
  { key: "open", label: "Abertas", color: "bg-slate-50" },
  { key: "in_progress", label: "Em execução", color: "bg-amber-50" },
  { key: "done", label: "Concluídas", color: "bg-blue-50" },
  { key: "validated", label: "Validadas", color: "bg-emerald-50" },
];

const emptyForm = {
  title: "", type: "preventive", priority: "medium",
  target_type: "machine", target_id: "", target_code: "",
  description: "", scheduled_date: "", technician_id: "", technician_name: "",
  estimated_hours: 1,
};

export default function CMMS() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [machines, setMachines] = useState([]);
  const [molds, setMolds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDlg, setShowDlg] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [o, s, u, m, mo] = await Promise.all([
        axios.get(`${API}/cmms/orders?limit=500`),
        axios.get(`${API}/cmms/stats`),
        axios.get(`${API}/users`).catch(() => ({ data: [] })),
        axios.get(`${API}/machines`),
        axios.get(`${API}/molds`),
      ]);
      setOrders(o.data); setStats(s.data);
      setUsers(u.data.filter(x => x.role !== "display"));
      setMachines(m.data); setMolds(mo.data);
    } catch { toast.error("Erro"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title) { toast.error("Título obrigatório"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.target_type === "machine") {
        const m = machines.find(x => x.id === payload.target_id);
        payload.target_code = m?.code || "";
      } else if (payload.target_type === "mold") {
        const m = molds.find(x => x.id === payload.target_id);
        payload.target_code = m?.code || "";
      }
      if (payload.technician_id) {
        const u = users.find(x => x.id === payload.technician_id);
        payload.technician_name = u?.name || u?.user_code || "";
      }
      if (!payload.scheduled_date) delete payload.scheduled_date;
      if (!payload.target_id) delete payload.target_id;
      await axios.post(`${API}/cmms/orders`, payload);
      toast.success("Ordem criada");
      setShowDlg(false); setForm(emptyForm); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  const doAction = async (mo, action) => {
    try {
      await axios.post(`${API}/cmms/orders/${mo.id}/${action}`);
      toast.success("Feito");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const remove = async (mo) => {
    if (!window.confirm(`Apagar ${mo.code}?`)) return;
    try { await axios.delete(`${API}/cmms/orders/${mo.id}`); toast.success("Apagada"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const grouped = STATUS_COLS.reduce((acc, s) => { acc[s.key] = orders.filter(o => o.status === s.key); return acc; }, {});

  if (loading) return <div className="flex justify-center p-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="cmms-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><Wrench className="h-8 w-8 text-blue-600" /> Manutenção (CMMS)</h1>
          <p className="text-sm text-muted-foreground mt-1">Ordens preventivas, corretivas, preditivas e inspeções — com checklist, técnicos, custos e MTTR.</p>
        </div>
        <div className="flex gap-2">
          <ExcelExportButton endpoint="maintenance-orders" label="Excel" />
          <Button onClick={() => setShowDlg(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="new-mo-btn"><Plus className="h-4 w-4 mr-1" /> Nova ordem</Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Abertas" value={stats?.by_status?.open || 0} icon={Clock} color="#64748b" />
        <KpiCard label="Em execução" value={stats?.by_status?.in_progress || 0} icon={Play} color="#f59e0b" />
        <KpiCard label="Concluídas (30d)" value={(stats?.by_status?.done || 0) + (stats?.by_status?.validated || 0)} icon={CheckCircle2} color="#16a34a" />
        <KpiCard label="Atrasadas" value={stats?.overdue_count || 0} icon={AlertTriangle} color="#dc2626" />
        <KpiCard label="Custo 30d" value={`${stats?.total_cost || 0} €`} icon={DollarSign} color="#0b1f3a" />
      </div>

      {/* Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {STATUS_COLS.map(col => (
          <div key={col.key} className={`rounded-lg p-3 ${col.color} border`}>
            <div className="font-semibold mb-2 flex justify-between text-sm">
              <span>{col.label}</span>
              <span className="text-xs text-slate-500">{grouped[col.key].length}</span>
            </div>
            <div className="space-y-2 max-h-[70vh] overflow-y-auto">
              {grouped[col.key].map(mo => {
                const t = TYPE_LABELS[mo.type] || { label: mo.type };
                return (
                  <div key={mo.id} className="bg-white border rounded-lg p-2 text-xs shadow-sm" data-testid={`mo-card-${mo.code}`}>
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-mono font-semibold">{mo.code}</span>
                      <span className="w-2 h-2 rounded-full" style={{ background: PRIORITY_COLORS[mo.priority] }} title={mo.priority} />
                    </div>
                    <div className="font-semibold text-sm text-slate-900 mb-1">{mo.title}</div>
                    <div className="flex flex-wrap gap-1 mb-2">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${t.color}`}>{t.label}</span>
                      {mo.target_code && <span className="px-1.5 py-0.5 rounded text-[10px] bg-slate-100">{mo.target_type}: {mo.target_code}</span>}
                    </div>
                    {mo.technician_name && <div className="text-[10px] text-slate-500">👷 {mo.technician_name}</div>}
                    {mo.scheduled_date && <div className="text-[10px] text-slate-500">📅 {new Date(mo.scheduled_date).toLocaleDateString("pt-PT")}</div>}
                    {mo.downtime_minutes ? <div className="text-[10px] text-slate-500">⏱ {mo.downtime_minutes} min</div> : null}
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {mo.status === "open" && <Button size="sm" className="h-6 text-[10px] px-2 bg-amber-600 hover:bg-amber-700" onClick={() => doAction(mo, "start")} data-testid={`start-${mo.code}`}><Play className="h-3 w-3 mr-1" />Iniciar</Button>}
                      {(mo.status === "open" || mo.status === "in_progress") && <Button size="sm" className="h-6 text-[10px] px-2 bg-blue-600 hover:bg-blue-700" onClick={() => doAction(mo, "complete")} data-testid={`complete-${mo.code}`}><CheckCircle2 className="h-3 w-3 mr-1" />Concluir</Button>}
                      {mo.status === "done" && <Button size="sm" className="h-6 text-[10px] px-2 bg-emerald-600 hover:bg-emerald-700" onClick={() => doAction(mo, "validate")} data-testid={`validate-${mo.code}`}><ShieldCheck className="h-3 w-3 mr-1" />Validar</Button>}
                      <button onClick={() => remove(mo)} className="text-red-500 hover:text-red-700 text-[10px]" title="Apagar"><Trash2 className="h-3 w-3" /></button>
                    </div>
                  </div>
                );
              })}
              {grouped[col.key].length === 0 && <div className="text-xs text-slate-400 text-center py-3">vazio</div>}
            </div>
          </div>
        ))}
      </div>

      {/* MTTR by target */}
      {stats?.mttr_by_target && Object.keys(stats.mttr_by_target).length > 0 && (
        <div className="bg-white border rounded-lg p-4">
          <h2 className="font-semibold mb-3">MTTR — Tempo médio de reparação (minutos)</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {Object.entries(stats.mttr_by_target).map(([tgt, min]) => (
              <div key={tgt} className="bg-slate-50 border rounded p-2 text-sm">
                <div className="text-xs text-slate-500">{tgt}</div>
                <div className="text-lg font-bold">{min} min</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dialog: nova ordem */}
      <Dialog open={showDlg} onOpenChange={setShowDlg}>
        <DialogContent className="max-w-xl" data-testid="new-mo-dialog">
          <DialogHeader><DialogTitle>Nova Ordem de Manutenção</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            <div><Label>Título *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="mo-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tipo</Label>
                <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="mo-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Prioridade</Label>
                <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="mo-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Baixa</SelectItem>
                    <SelectItem value="medium">Média</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="critical">Crítica</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Alvo</Label>
                <Select value={form.target_type} onValueChange={v => setForm({ ...form, target_type: v, target_id: "" })}>
                  <SelectTrigger data-testid="mo-target-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="machine">Máquina</SelectItem>
                    <SelectItem value="mold">Molde</SelectItem>
                    <SelectItem value="general">Geral</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{form.target_type === "machine" ? "Máquina" : form.target_type === "mold" ? "Molde" : "—"}</Label>
                <Select value={form.target_id} onValueChange={v => setForm({ ...form, target_id: v })} disabled={form.target_type === "general"}>
                  <SelectTrigger data-testid="mo-target"><SelectValue placeholder={form.target_type === "general" ? "N/A" : "Escolhe..."} /></SelectTrigger>
                  <SelectContent>
                    {(form.target_type === "machine" ? machines : molds).map(x => <SelectItem key={x.id} value={x.id}>{x.code} — {x.name || x.description}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Técnico</Label>
                <Select value={form.technician_id} onValueChange={v => setForm({ ...form, technician_id: v })}>
                  <SelectTrigger data-testid="mo-tech"><SelectValue placeholder="Opcional" /></SelectTrigger>
                  <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.user_code})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Data agendada</Label><Input type="datetime-local" value={form.scheduled_date} onChange={e => setForm({ ...form, scheduled_date: e.target.value })} data-testid="mo-date" /></div>
            </div>
            <div><Label>Descrição</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Horas estimadas</Label><Input type="number" step="0.5" value={form.estimated_hours} onChange={e => setForm({ ...form, estimated_hours: parseFloat(e.target.value) || 0 })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDlg(false)}>Cancelar</Button>
            <Button onClick={create} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-mo-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white border rounded-lg p-3">
      <div className="flex items-center gap-1 text-[10px] text-slate-500 uppercase tracking-wide"><Icon className="h-3 w-3" />{label}</div>
      <div className="text-2xl font-black mt-0.5" style={{ color }}>{value ?? "-"}</div>
    </div>
  );
}
